import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  generateVideo,
  setColors,
  setNewProjectName,
  setProjectInput,
} from '../../element-editor/store/editor.actions';
import {
  selectColors,
  selectHasColors,
} from '../../element-editor/store/editor.selectors';
import { ColorEditorOverlayService } from '../../shared/overlays/color-editor-overlay/color-editor-overlay.service';
import { HttpService, RequestType } from '../../store/http/http.service';
import { ShapeService } from '../../element-editor/shape/shape.service';
import { ResourceData } from '../../elements/resource/resource.types';
import { Router } from '@angular/router';
import { IdToIRI, IRIToId } from '../../util/iri';
import { GeneralShapeDescriptor } from '../../elements/resource/types/shape.type';
import { selectNewProjectName } from '../../element-editor/store/selector/editor.selector';
import { selectCurrentOrganizationID } from '../../organisation/organisation.selector';
import { UserService } from '../../user/user.service';

export type MediaKind = 'image' | 'video' | 'doc';
export type DocSubType = 'pdf' | 'ppt';

export interface PromptFile {
  type: MediaKind;
  url: string;
  name?: string;
  docType?: DocSubType;
  durationSec?: number;
  file?: File;
}
export interface Prompt {
  text?: string;
  files: PromptFile[];
}

@Component({
  selector: 'app-new-project-component',
  templateUrl: './new-project-component.component.html',
  styleUrls: ['./new-project-component.component.scss'],
})
export class NewProjectComponentComponent implements OnInit, OnDestroy {
  @ViewChild('overlayCloseBtn') overlayCloseBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('imageInput') imageInput?: ElementRef<HTMLInputElement>;
  @ViewChild('videoInput') videoInput?: ElementRef<HTMLInputElement>;
  @ViewChild('docInput') docInput?: ElementRef<HTMLInputElement>;

  promptText = '';
  file?: File;
  filePreviewUrl?: string;
  fileType?: 'image' | 'video';

  imageUploadState: 'initial' | 'dragging' | 'dropped' = 'initial';

  // Colors selectors
  colors$: Observable<string[]> = this.store.select(selectColors);
  hasColors$: Observable<boolean> = this.store.select(selectHasColors);

  errorMsg = '';

  pendingPrompt: Prompt = { text: '', files: [] };
  addedPrompts: Prompt[] = [];

  // Media model for overlay + thumbnails (see OverlayItem interface above)

  // Overlay preview state
  overlayOpen = false;
  overlayIndex = 0;
  overlayItems: PromptFile[] = [];
  lastTriggerEl: HTMLElement | null = null;

  submissionLoading = false;

  newProjectName = '';
  get textAreaPlaceHolder() {
    return this.pendingPrompt.files.length
      ? 'Describe the image'
      : 'Tell us about your story';
  }

  async submitNewProject() {
    // -- // -- // -- //
    this.submissionLoading = true;
    if (this.pendingPrompt || this.pendingPrompt.files.length) {
      this.addedPrompts.push({
        text: this.pendingPrompt.text?.trim() || undefined,
        files: this.pendingPrompt.files,
      });
    }

    const filesToSave: ResourceData[] = [];
    // -- // -- // -- //
    const uploadPromises = [];

    const projectIRI = this.shapeService.getNewIRI();
    const projectID = IRIToId(projectIRI);

    const project = {
      IRI: projectIRI,
      type: 'nw:Project',
      literals: {
        label:
          this.newProjectName ||
          'project-' + Math.random().toString().slice(2, 5),
        descriptor: {},
      },
      relationships: {
        isUnder: IdToIRI(this.currentOrgId),
      },
    };

    const role = {
      IRI: this.shapeService.getNewIRI(),
      type: 'nw:Role',
      literals: {
        roleType: 'owner',
      },
      relationships: {
        inProject: projectIRI,
      },
    };

    let index = 0;
    for (const prompt of this.addedPrompts) {
      const { text, files } = prompt;

      if (text) {
        filesToSave.push({
          IRI: this.shapeService.getNewIRI(),
          type: 'nw:File',
          literals: {
            label: 'text',
            descriptor: { index, preview: { text } } as GeneralShapeDescriptor,
          },
          relationships: { isIn: projectIRI },
        });
        index++;
      }
      // const fileData: { id: string; type: MediaKind }[] = [];

      const images = [];
      const docs = [];
      const videos = [];
      for (const file of files) {
        const mediaIRI = this.shapeService.getNewIRI();
        const imageID = IRIToId(mediaIRI);
        uploadPromises.push(this.http.saveFileToS3(file.file, imageID));
        // -- //
        switch (file.type) {
          case 'image':
            images.push(imageID);
            break;
          case 'doc':
            docs.push(imageID);
            break;
          case 'video':
            videos.push(imageID);
            break;
        }
      }

      if (images.length) {
        filesToSave.push({
          IRI: this.shapeService.getNewIRI(),
          type: 'nw:File',
          literals: {
            label: 'images',
            descriptor: {
              index,
              preview: { images },
            } as GeneralShapeDescriptor,
          },
          relationships: { isIn: projectIRI },
        });
        index++;
      }

      if (docs.length) {
        filesToSave.push({
          IRI: this.shapeService.getNewIRI(),
          type: 'nw:File',
          literals: {
            label: 'docs',
            descriptor: {
              index,
              preview: { docs },
            } as GeneralShapeDescriptor,
          },
          relationships: { isIn: projectIRI },
        });
        index++;
      }

      for (const video of videos) {
        filesToSave.push({
          IRI: this.shapeService.getNewIRI(),
          type: 'nw:File',
          literals: {
            label: 'video',
            descriptor: {
              index,
              preview: { video },
            } as GeneralShapeDescriptor,
          },
          relationships: { isIn: projectIRI },
        });
        index++;
      }
    }

    // console.log('.......', posts); //

    const fileSavePromise = this.http
      .requestCall({
        type: RequestType.PATCH,
        path: 'shape',
        body: {
          file: {},
          posts: [project, role, ...filesToSave],
          patches: [],
          deletes: [],
        },
      })
      .toPromise();

    console.log({ project, filesToSave });
    this.store.dispatch(setProjectInput({ project, files: filesToSave }));
    // -- // -- // -- //
    this.store.dispatch(setNewProjectName({ name: '' }));
    const time = performance.now();
    await Promise.all([...uploadPromises, fileSavePromise]);

    const diff = performance.now() - time;

    if (diff < 30000) {
      await new Promise(resolve => setTimeout(resolve, 3000 - diff));
    }

    this.router.navigate(['p', projectID]);

    this.submissionLoading = false;

    // Redirect
  }

  keyup(event: KeyboardEvent) {
    // -- // -- //
    if (event.key === 'Enter') {
      this.submitPrompt();
    }
  }

  hover(hover: boolean) {
    console.log('hover-event', hover);
    if (!hover) {
      this.imageUploadState = 'initial';
      return;
    }
    this.imageUploadState = 'dragging';
  }

  previewUrls: string[] = [];

  pendingFiles: File[] = [];

  async onFileDropped(event: { files?: FileList | File[] } | null) {
    this.imageUploadState = 'dropped';
    // -- // -- // -- //
    const raw = event?.files;
    const files: File[] = raw
      ? raw instanceof FileList
        ? Array.from(raw)
        : (raw as File[])
      : [];
    if (!files.length) return;

    for (const file of files) {
      await this.processFile(file);
    }

    this.rebuildOverlayItems();
  }

  triggerFileInput(kind: 'image' | 'video' | 'doc') {
    const el =
      kind === 'image'
        ? this.imageInput?.nativeElement
        : kind === 'video'
          ? this.videoInput?.nativeElement
          : this.docInput?.nativeElement;
    el?.click();
  }

  async onFilesSelected(_kind: 'image' | 'video' | 'doc', event: Event) {
    const input = event.target as HTMLInputElement;
    const list = input.files;
    if (!list || list.length === 0) {
      input.value = '';
      return;
    }

    // mirror drag-and-drop flow
    this.imageUploadState = 'dropped';

    const files: File[] = Array.from(list);
    for (const file of files) {
      await this.processFile(file);
    }

    this.rebuildOverlayItems();

    // allow selecting the same files again later
    input.value = '';
  }

  currentOrgId$ = this.store.select(selectCurrentOrganizationID);
  currentOrgId: string;
  constructor(
    private store: Store<unknown>,
    private colorOverlay: ColorEditorOverlayService,

    private http: HttpService,

    private shapeService: ShapeService,
    public router: Router,
    private userService: UserService,
  ) {
    this.store.select(selectNewProjectName).subscribe(name => {
      this.newProjectName = name;
    });
    this.currentOrgId$.subscribe(id => {
      this.currentOrgId = id;
    });
  }

  async onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      this.file = undefined;
      this.filePreviewUrl = undefined;
      this.fileType = undefined;
      return;
    }
    await this.processFile(file);
    this.rebuildOverlayItems();
  }

  submitPrompt() {
    const text = (this.pendingPrompt.text || this.promptText || '').trim();
    const files = this.pendingPrompt.files;

    if (!text && files.length === 0) return;

    this.addedPrompts.push({ text: text || undefined, files: [...files] });

    // reset pending
    this.pendingPrompt = { text: '', files: [] };
    this.promptText = '';
    this.previewUrls = [];
    this.imageUploadState = 'initial';

    this.rebuildOverlayItems();
  }

  removePendingFile(index: number, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const f = this.pendingPrompt.files[index];
    if (!f) return;
    // Revoke local object URLs for non-image previews if needed
    if (
      (f.type === 'video' || f.type === 'doc') &&
      f.url?.startsWith('blob:')
    ) {
      try {
        URL.revokeObjectURL(f.url);
      } catch {
        /* noop */
      }
    }
    this.pendingPrompt.files.splice(index, 1);
    this.rebuildOverlayItems();

    if (this.overlayOpen) {
      if (this.overlayIndex >= this.overlayItems.length) {
        this.overlayIndex = Math.max(0, this.overlayItems.length - 1);
      }
      if (this.overlayItems.length === 0) {
        this.closeOverlay();
      }
    }
  }

  // Overlay lifecycle
  ngOnInit(): void {
    this.rebuildOverlayItems();
  }

  ngOnDestroy(): void {
    const allFiles: PromptFile[] = [
      ...this.pendingPrompt.files,
      ...this.addedPrompts.flatMap(p => p.files || []),
    ];
    allFiles.forEach(f => {
      if (
        (f.type === 'video' || f.type === 'doc') &&
        f.url?.startsWith('blob:')
      ) {
        try {
          URL.revokeObjectURL(f.url);
        } catch (e) {
          /* noop */
        }
      }
    });
  }

  private rebuildOverlayItems() {
    const promptMedia: PromptFile[] = [];
    for (const p of this.addedPrompts) {
      (p.files || []).forEach(f => promptMedia.push({ ...f }));
    }
    // pending first
    this.overlayItems = [...this.pendingPrompt.files, ...promptMedia];
  }

  openOverlay(index: number, trigger?: EventTarget | null) {
    if (!this.overlayItems.length) return;
    this.overlayIndex = Math.max(
      0,
      Math.min(index, this.overlayItems.length - 1),
    );
    this.overlayOpen = true;
    this.lastTriggerEl = (trigger as HTMLElement) || null;
    // focus close button shortly after render
    setTimeout(() => this.overlayCloseBtn?.nativeElement?.focus(), 0);
    this.preloadNeighbors();
  }

  closeOverlay() {
    this.pauseOverlayVideo();
    this.overlayOpen = false;
    const el = this.lastTriggerEl;
    this.lastTriggerEl = null;
    if (el) {
      el.focus();
    }
  }

  private pauseOverlayVideo() {
    const v = document.querySelector(
      '.overlay-content video',
    ) as HTMLVideoElement | null;
    if (v) {
      try {
        v.pause();
      } catch (e) {
        /* noop */
      }
    }
  }

  next() {
    if (this.overlayIndex < this.overlayItems.length - 1) {
      this.pauseOverlayVideo();
      this.overlayIndex++;
      this.preloadNeighbors();
    }
  }

  /** Extracts a "uuid" from an IRI (last segment after / or #). */
  private iriSuffix(iri: string): string {
    const m = iri.match(/[^/#]+$/);
    return m ? m[0] : iri;
  }

  /** Converts a data URL to base64 payload (no mime header). */
  private dataUrlToBase64(dataUrl: string): string {
    const commaIdx = dataUrl.indexOf(',');
    return commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
  }

  /** Fetches a blob and returns base64 string. Works for blob: or http(s) URLs. */
  private async urlToBase64(url: string): Promise<string> {
    const res = await fetch(url);
    const blob = await res.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++)
      binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }

  /** Best-effort file extension from name or mime. */
  private guessExtension(name?: string, mime?: string): string {
    if (name && /\.[a-z0-9]+$/i.test(name))
      return name.split('.').pop()!.toLowerCase();
    if (!mime) return 'bin';
    if (mime.startsWith('image/')) return mime.split('/')[1];
    if (mime.startsWith('video/')) return mime.split('/')[1];
    if (mime === 'application/pdf') return 'pdf';
    if (mime.includes('presentation')) return 'pptx';
    if (mime.includes('powerpoint')) return 'ppt';
    return 'bin';
  }

  prev() {
    if (this.overlayIndex > 0) {
      this.pauseOverlayVideo();
      this.overlayIndex--;
      this.preloadNeighbors();
    }
  }

  onOverlayKeydown(e: KeyboardEvent) {
    if (!this.overlayOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeOverlay();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    }
  }

  onBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      this.closeOverlay();
    }
  }

  get currentItem() {
    return this.overlayItems[this.overlayIndex];
  }

  private overlayIndexForPromptFile(
    promptIndex: number,
    fileIndex: number,
  ): number | null {
    let idx = this.pendingPrompt.files.length; // pending first
    for (let i = 0; i < this.addedPrompts.length; i++) {
      const files = this.addedPrompts[i]?.files || [];
      if (files.length) {
        if (i === promptIndex) return idx + fileIndex;
        idx += files.length;
      }
    }
    return null;
  }

  onPromptFileClick(
    promptIndex: number,
    fileIndex: number,
    target: EventTarget,
  ) {
    const oi = this.overlayIndexForPromptFile(promptIndex, fileIndex);
    if (oi !== null) this.openOverlay(oi, target);
  }

  private preloadNeighbors() {
    const prev = this.overlayItems[this.overlayIndex - 1];
    const next = this.overlayItems[this.overlayIndex + 1];
    [prev, next].forEach(item => {
      if (!item) return;
      if (item.type === 'image') {
        const img = new Image();
        img.src = item.url;
      }
      // do not pre-buffer videos, just let metadata load when selected
    });
  }

  private async processFile(file: File) {
    this.errorMsg = '';
    const mime = file.type || '';
    if (mime.startsWith('image/')) {
      await new Promise<void>(resolve => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            const url = reader.result as string;
            this.previewUrls.push(url);
            this.pendingPrompt.files.push({
              type: 'image',
              url,
              file,
              name: file.name,
            });
          }
          resolve();
        };
        reader.readAsDataURL(file);
      });
      return;
    }

    if (mime.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      try {
        const dur = await this.getVideoDuration(url);
        if (dur > 120) {
          URL.revokeObjectURL(url);
          this.showError('Video must be 2 minutes or less.');
          return;
        }
        this.pendingPrompt.files.push({
          type: 'video',
          url,
          name: file.name,
          file,
          durationSec: Math.round(dur),
        });
      } catch {
        URL.revokeObjectURL(url);
        this.showError('Could not read video metadata.');
      }
      return;
    }

    if (
      mime === 'application/pdf' ||
      mime === 'application/vnd.ms-powerpoint' ||
      mime ===
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ) {
      const url = URL.createObjectURL(file);
      const docType: 'pdf' | 'ppt' = mime === 'application/pdf' ? 'pdf' : 'ppt';
      this.pendingPrompt.files.push({
        type: 'doc',
        url,
        name: file.name,
        docType,
        file,
      });
      return;
    }
  }

  private getVideoDuration(url: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.src = url;
      v.onloadedmetadata = () => {
        const d = v.duration ?? 0;
        resolve(d);
      };
      v.onerror = () => reject(new Error('Cannot read video metadata'));
    });
  }

  private showError(msg: string) {
    this.errorMsg = msg;
    setTimeout(() => (this.errorMsg = ''), 4000);
  }

  generateVideo() {
    const prompt = this.promptText?.trim();
    if (!prompt) return;
    this.store.dispatch(generateVideo({ prompt }));
  }

  async openColorEditor() {
    const initial = await new Promise<string[]>(resolve => {
      const sub = this.colors$.pipe(take(1)).subscribe(colors => {
        resolve(colors || []);
        sub?.unsubscribe();
      });
    });
    const result = await this.colorOverlay.open(initial);
    if (result) {
      this.store.dispatch(setColors({ colors: result }));
    }
  }
}
